<template>
  <lbs-dialog v-if="isVisible"
              class="render-panel"
              :title="$t('image-adjustments')"
              @close="isVisible = false">
    <div slot="body"
         v-if="currentState"
         class="container">
      <slider :current-state="currentState"
              prop-name="brightness"/>
      <slider :current-state="currentState"
              prop-name="contrast"/>
      <slider :current-state="currentState"
              prop-name="gamma"/>
    </div>
    <div slot="footer">
      <b-button @click="store.reset">{{ $t('reset') }}</b-button>
      <b-dropdown right
                  :text="$t('presets')"
                  @hidden="isNewPresetFormVisible = false"
      >
        <b-dropdown-item v-for="preset in store.presets"
                         :key="preset.name"
                         class="preset-link"
                         @click="store.applyPreset(preset)"
        >
          <b-btn @click="store.deletePreset(preset.name)"
                 size="sm"
                 variant="white"
                 :title="$t('delete-preset')"
          >
            <icon name="regular/trash-alt"
                  scale="1"/>
          </b-btn>
          <span>
            {{ preset.name }}
          </span>
          <span class="icon-container">
            <icon v-show="preset === store.selectedPreset"
                  name="check"/>
          </span>
        </b-dropdown-item>
        <b-dropdown-divider/>
        <b-dropdown-form @submit.prevent.stop>
          <b-button variant="outline-primary"
                    @click="showSavePresetForm"
          >
            {{ $t('save-preset') }}
          </b-button>
          <b-form-input v-model="newPresetName"
                        v-show="isNewPresetFormVisible"
                        :disabled="showOverwriteWarning"
                        ref="newPresetInput"
                        :placeholder="$t('name-preset')"
                        class="mt-2 mb-2"
                        @blur="saveNewPreset"
                        @keydown.enter="saveNewPreset"
          />
        </b-dropdown-form>
        <b-alert variant="danger"
                 :show="showOverwriteWarning">
          <p>{{ $t('confirm-overwrite') }}</p>
          <b-button variant="outline-secondary"
                    @click="overwritePreset">{{ $t('overwrite-yes') }}</b-button>
          <b-button variant="outline-secondary"
                    @click="cancelOverwritePreset">{{ $t('overwrite-no') }}</b-button>
        </b-alert>
      </b-dropdown>
    </div>
  </lbs-dialog>
</template>

<script>
  import LbsDialog from '../../components/vue/lbs-dialog';
  import Slider from './Slider';
  import Icon from 'vue-awesome/components/Icon';
  import 'vue-awesome/icons/regular/trash-alt';
  import { createLogger } from 'lbs-pi-handy/src/general-purpose/logger';

  const logger = createLogger('ImageAdjustContainer');

  export default {
    name: 'ImageAdjustContainer',

    i18nOptions: {
      keyPrefix: 'uScope.extensions.image-adjust'
    },

    components: {
      Slider,
      LbsDialog,
      Icon
    },

    props: {
      store: {
        type: Object,
        required: true
      }
    },

    data () {
      return {
        // Tracks if the dialog is visible
        isVisible: false,
        showOverwriteWarning: false,
        // Preset saver form
        isNewPresetFormVisible: false,
        newPresetName: ''
      };
    },

    computed: {
      currentState () {
        return this.store.currentState;
      }
    },

    methods: {
      toggleVisibility () {
        this.isVisible = !this.isVisible;
      },
      showSavePresetForm () {
        if (this.isNewPresetFormVisible === true) {
          return;
        }
        this.isNewPresetFormVisible = true;
        this.newPresetName = '';
        this.$nextTick(this.$refs.newPresetInput.focus);
      },
      overwritePreset () {
        this.store.saveAsPreset(this.newPresetName, true);
        this.showOverwriteWarning = false;
      },
      cancelOverwritePreset () {
        this.showOverwriteWarning = false;
        this.newPresetName = '';
        this.$nextTick(this.$refs.newPresetInput.focus);
      },
      async saveNewPreset (e, forceSave) {
        if (e instanceof KeyboardEvent) {
          // we only want one entry to this function. if "enter" triggers, the form hides triggering re-entry
          // and a double-save, which is pops up an awkward "are you sure you want to overwrite?" message.
          // Triggering the blur when the user presses "enter" prevents this problem.
          e.target.blur();
          return;
        }

        try {
          this.store.saveAsPreset(this.newPresetName, forceSave);
          this.isNewPresetFormVisible = false;
        } catch (ex) {
          if (ex.message === 'ALREADY_EXISTS') {
            this.showOverwriteWarning = true;
          } else {
            logger.error(ex);
          }
        }
      }
    },
    mounted () {
      this.$root.$on('bv::dropdown::hide', () => {
        this.showOverwriteWarning = false;
      });
    }
  };
</script>

<style lang="scss" scoped>
@import "~lbs-pi-web-styles/lib/variables";
@import "image-adjust";

.preset-link >>> a { // >>> allows targeting child component selectors
  button {
    visibility: hidden;
    margin-left: -20px;
    margin-top: -4px;
  }
  &:hover button {
    visibility: visible;
    &:hover{
      color: #1c78a6;
    }
  }
  span:hover {
      color: #1c78a6;
      text-decoration: underline;
  }
}
.btn-outline-primary {
  white-space: nowrap;
}
.alert.alert-danger {
  padding-right: 0;
  margin-bottom: 0;
}
.alert:not(.alert-dismissible).alert-danger:after {
  background: none;
}
.b-dropdown-form:focus {
  outline: none !important;
}
</style>
